import firebase from "firebase/compat";
import { getRandomId } from "./Haim/utils";
import { ALLOWED_LISTING_FILE_MIME_TYPES } from "../@types/sharedSchema";
import type { AllowedListingFileMimeType } from "../@types/sharedSchema";
import type { FileOrFilePreview, FileWithId, FilePreview } from "../@types/listings/fileData";

export const DEFAULT_MAX_FILE_UPLOAD = 5;
export const DEFAULT_MAX_FILE_SIZE_MB = 20;

/**
 * The accepted mime types live in `sharedSchema.ts` — they type
 * `ListingFilePreview.type`, which is part of the persisted listing document,
 * so the server needs the same union. Re-exported under the historical names
 * (as local bindings, since they're also used below in this module).
 */
export const ALLOWED_FILE_MIME_TYPES = ALLOWED_LISTING_FILE_MIME_TYPES;
export type AllowedFIleMimeType = AllowedListingFileMimeType;

export function isValidFile(
  name: string,
  type: string,
  bytes: number,
  maxFileSizeMB: number = DEFAULT_MAX_FILE_SIZE_MB,
  allowedFileMimeTypes: Readonly<string[]> = ALLOWED_FILE_MIME_TYPES
): boolean {
  const isFileHasExtension = name.split(".").pop()?.toLowerCase();

  if (!isFileHasExtension) return false;

  const isValidFileMimeType = allowedFileMimeTypes.includes(type);

  if (!isValidFileMimeType) return false;

  const isValidFileSize = bytes < maxFileSizeMB * Math.pow(1024, 2);

  if (!isValidFileSize) return false;

  return true;
}

type FilesData = {
  filesPreviewData: FilePreview[];
  selectedFilesData: FileWithId[];
};

export function getFilesData(
  files: FileList | null,
  currentFiles: number,
  maxFileUpload: number = DEFAULT_MAX_FILE_UPLOAD,
  maxFileSizeMB: number = DEFAULT_MAX_FILE_SIZE_MB,
  allowedFileMimeTypes: Readonly<string[]> = ALLOWED_FILE_MIME_TYPES
): FilesData | null {
  if (!files || !files.length) return null;

  const singleEditingMode = currentFiles === undefined;

  const rawImages =
    singleEditingMode ||
    !(currentFiles === maxFileUpload || files.length > maxFileUpload - currentFiles)
      ? Array.from(files).filter(({ name, size, type }) =>
          isValidFile(name, type, size, maxFileSizeMB, allowedFileMimeTypes)
        )
      : null;

  if (!rawImages || !rawImages.length) return null;

  const imagesId = rawImages.map(({ name }) => ({
    id: getRandomId(20),
    name: name
  }));

  const filesPreviewData = rawImages.map((image, index) => ({
    id: imagesId[index].id,
    src: URL.createObjectURL(image),
    name: imagesId[index].name ?? image.name,
    type: image.type as AllowedFIleMimeType,
    size: image.size
  }));

  const selectedFilesData = rawImages.map((image, index) =>
    renameFile(image, imagesId[index].id, imagesId[index].name)
  );

  return { filesPreviewData, selectedFilesData };
}

export function renameFile(file: File, newId: string, newName: string | null): FileWithId {
  return Object.assign(
    newName
      ? new File([file], newName, {
          type: file.type,
          lastModified: file.lastModified
        })
      : file,
    { id: newId }
  );
}

export async function uploadFiles(
  files: FileOrFilePreview[],
  userId: string
): Promise<null | FilePreview[]> {
  if (!files.length) return null;

  const filesPreview: FilePreview[] = await Promise.all(
    files.map(async (file) => {
      if ("src" in file) return file;

      const { id, type, name, size } = file;

      const storageRef = firebase.storage().ref(`/real-estate-listings/${userId}/${id}`);
      const uploadTask = storageRef.put(file);

      await uploadTask;

      const src = (await uploadTask.snapshot.ref.getDownloadURL()) as string;

      return {
        id: id,
        src: src,
        name: name,
        type: type as AllowedFIleMimeType,
        size: size
      };
    })
  );

  return filesPreview;
}
